import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, Loader2, Copy, Link as LinkIcon, RefreshCw, Circle, CheckCircle, Users, Download, Archive, ArchiveRestore, RotateCcw, ChevronDown, Play, X, AlertTriangle } from 'lucide-react';
import { JitsiCall } from '@/components/interview/JitsiCall';
import { useInterviewRecorder } from '@/hooks/useInterviewRecorder';
import { getAuthHeaders } from '@/hooks/useAuth';
import { getInterview, hostJoin, recordStart, recordStop, regenerateLink, remergeTake, patchTake, harvestNow, type Interview, type InterviewTake, type RecordMode } from '@/services/interviewApi';
import { useToast } from '@/hooks/use-toast';
import { joinCastingRoom, sendCastingRoomMessage, leaveCastingWs, onCastingMessage } from '@/services/castingWs';
import GimbalPanel from '@/components/interview/GimbalPanel';

const MODES: { key: RecordMode; label: string }[] = [
  { key: 'guest_only', label: 'Guest only' },
  { key: 'side_by_side', label: 'Side by side' },
  { key: 'guest_plus_host_audio', label: 'Guest + my audio' },
];

// What the spinner is actually waiting on. "Waiting for clip…" read as "the
// upload is stuck" when the phone had already finished: the clip lands in
// casting's recordings first and the worker pulls it into the session on a
// 45-second sweep, and then the merge (your audio, or both cameras) runs before
// the web copy exists. Say which step it is.
const waitingLabel = (t: InterviewTake, recording: boolean): string => {
  const hasClip = !!t.guestClip?.s3Key;
  if (!hasClip) return recording ? 'Recording…' : 'Uploaded on the phone? Pulling it into the session, usually under a minute.';
  if (t.mode === 'guest_plus_host_audio') return 'Clip is in. Mixing in your audio…';
  if (t.mode === 'side_by_side') return 'Clip is in. Combining both cameras…';
  return 'Clip is in. Encoding a web copy…';
};

const INVITE_BLURB = "Click the link a few minutes early. Use Chrome on a computer if you can, plug in headphones, and find a quiet spot. You'll type your name and be straight in.";

const card: React.CSSProperties = { padding: 16, borderRadius: 'var(--hj-r-md)', background: 'var(--hj-surface-1)', border: '1px solid var(--hj-hairline)' };
const ghost: React.CSSProperties = { padding: '7px 12px', borderRadius: 999, background: 'transparent', border: '1px solid var(--hj-hairline)', color: 'var(--hj-ink-2)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6 };

const InterviewHostPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [interview, setInterview] = useState<Interview | null>(null);
  const [guestUrl, setGuestUrl] = useState('');
  const [join, setJoin] = useState<{ domain: string; roomName: string; jwt: string } | null>(null);
  // Guest video with the host's audio is what a reader session (and most
  // interviews) actually want: the guest's take, with the other side's lines on it.
  const [mode, setMode] = useState<RecordMode>('guest_plus_host_audio');
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [modalGroup, setModalGroup] = useState<string | null>(null);
  const [modalTakeId, setModalTakeId] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval>>();
  const rec = useInterviewRecorder();
  // The take this page's own recorder is uploading, so its card can show our %.
  const hostTakeRef = useRef<string | null>(null);
  // App-guest signals relayed over the casting session room.
  const [appRecLiveAt, setAppRecLiveAt] = useState(0);   // last local-record-started heartbeat
  const [appUploadPct, setAppUploadPct] = useState<number | null>(null);
  const [, setTick] = useState(0);                        // 1s re-render for the elapsed clock
  const startWarnRef = useRef<ReturnType<typeof setTimeout>>();
  const startRetryRef = useRef<ReturnType<typeof setInterval>>();
  const stopRetryRef = useRef<ReturnType<typeof setInterval>>();

  useEffect(() => {
    if (!id) return;
    getInterview(id).then(({ interview, guestUrl }) => {
      setInterview(interview); setGuestUrl(guestUrl);
    }).catch(() => navigate('/my-store')).finally(() => setLoading(false));
    // Bare join is the intended mode — local recording needs no Jitsi moderator.
    hostJoin(id).then(setJoin).catch(() => {});
    pollRef.current = setInterval(() => { getInterview(id).then(({ interview }) => setInterview(interview)).catch(() => {}); }, 2000);
    return () => { clearInterval(pollRef.current); clearTimeout(startWarnRef.current); clearInterval(startRetryRef.current); clearInterval(stopRetryRef.current); leaveCastingWs(); };
  }, [id]);

  // Listen on the casting session room for the phone's reports. Reactive —
  // the shadow session is often created AFTER this page loads (when the guest's
  // phone first fetches the app link), so a load-time-only join would miss it
  // and none of the camera/gimbal/upload reports would ever arrive.
  useEffect(() => {
    const sid = interview?.casting?.sessionId;
    if (sid) joinCastingRoom(`talent-session-${sid}`);
  }, [interview?.casting?.sessionId]);

  // App-guest signals: recording heartbeat + upload progress on the session room.
  useEffect(() => onCastingMessage((m) => {
    if (m.type === 'local-record-started') setAppRecLiveAt(Date.now());
    if (m.type === 'local-record-stopped') setAppRecLiveAt(0);
    if (m.type === 'upload-started') setAppUploadPct(0);
    if (m.type === 'upload-progress' && typeof m.progress === 'number') setAppUploadPct(Math.min(99, Math.round(m.progress)));
    if (m.type === 'upload-complete') {
      setAppUploadPct(null);
      // Don't wait for the 45-second sweep: pull the clip in now, and once more
      // a few seconds later in case casting hadn't finished writing its record.
      if (id) { harvestNow(id); setTimeout(() => harvestNow(id), 6000); }
    }
  }), []);

  // 1s tick while recording so the elapsed clock advances.
  useEffect(() => {
    if (interview?.recordControl?.state !== 'recording') return;
    const itv = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(itv);
  }, [interview?.recordControl?.state]);

  const copy = (text: string, label: string) => { navigator.clipboard.writeText(text); toast({ title: label }); };
  const gs = interview?.guestState;
  const rc = interview?.recordControl;
  const recording = rc?.state === 'recording';
  const guestRecRef = useRef(false);
  const appRecLiveAtRef = useRef(0);
  guestRecRef.current = !!gs?.recording;
  appRecLiveAtRef.current = appRecLiveAt;

  const doStart = async () => {
    if (!id) return;
    try {
      const { recordControl, serverNowMs } = await recordStart(id, mode);
      // Resend the trigger every 2s until the far side confirms (app heartbeat
      // or web guestState) — a single send can vanish on a just-reconnected or
      // half-dead socket. The app dedupes starts, so resends are harmless.
      clearTimeout(startWarnRef.current);
      clearInterval(startRetryRef.current);
      const sendTrigger = () => {
        if (interview?.casting?.recordId) {
          sendCastingRoomMessage(`talent-${interview.casting.recordId}`, { type: 'start-local-recording', group: interview.casting.groupId });
        }
      };
      sendTrigger();
      startRetryRef.current = setInterval(() => {
        const confirmed = guestRecRef.current || (Date.now() - appRecLiveAtRef.current < 4000);
        if (confirmed) { clearInterval(startRetryRef.current); return; }
        sendTrigger();
      }, 2000);
      startWarnRef.current = setTimeout(() => {
        clearInterval(startRetryRef.current);
        const appLive = Date.now() - appRecLiveAtRef.current < 4000;
        if (!guestRecRef.current && !appLive) {
          toast({ title: "Guest recording didn't start", description: 'Ask your guest to rejoin (or reopen the invite in the app), then try again.', variant: 'destructive' });
          // Kill the take right away instead of letting it spin — and reset the
          // server record state so the next Record press starts clean.
          if (recordControl.takeId) patchTake(id, recordControl.takeId, { failedNoStart: true } as any).catch(() => {});
          recordStop(id).catch(() => {});
          refresh();
        }
      }, 12000);
      // Host records its own camera only in the modes that use it.
      if (mode !== 'guest_only' && recordControl.takeId) {
        const offset = serverNowMs - Date.now();
        // Never swallow this: when the host side can't record, the take otherwise
        // spins on "Mixing in your audio" forever. Say so, and switch the take to
        // the guest's clip alone so it still finishes.
        const takeId = recordControl.takeId;
        hostTakeRef.current = takeId;
        const hostFailed = () => {
          toast({ title: "Your audio didn't upload", description: "This take will use your guest's video and audio.", variant: 'destructive' });
          patchTake(id, takeId, { hostFailed: true } as any).then(refresh).catch(() => {});
        };
        rec.start({ base: `/api/interviews/${id}/upload`, headers: getAuthHeaders(), takeId, role: 'host', includeVideo: true, startAtEpochMs: recordControl.startAtEpochMs, clockOffset: offset, onFail: hostFailed }).catch(hostFailed);
      }
    } catch (e: any) { toast({ title: "Couldn't start", description: e.message, variant: 'destructive' }); }
  };
  const doStop = async () => {
    if (!id) return;
    clearTimeout(startWarnRef.current);
    clearInterval(startRetryRef.current);
    if (interview?.casting?.recordId) {
      const room = `talent-${interview.casting.recordId}`;
      sendCastingRoomMessage(room, { type: 'stop-local-recording' });
      // Resend stop while the app's 1s recording heartbeat keeps arriving.
      clearInterval(stopRetryRef.current);
      let tries = 0;
      stopRetryRef.current = setInterval(() => {
        const stillRecording = Date.now() - appRecLiveAtRef.current < 3000;
        if (!stillRecording || ++tries > 6) { clearInterval(stopRetryRef.current); return; }
        sendCastingRoomMessage(room, { type: 'stop-local-recording' });
      }, 2000);
    }
    if (rec.recording) await rec.stop().catch(() => {});
    await recordStop(id).catch(() => {});
  };
  const doRegen = async () => {
    if (!id || !confirm('Make a new guest link? The old one stops working.')) return;
    try { const { guestUrl } = await regenerateLink(id); setGuestUrl(guestUrl); toast({ title: 'New link created' }); } catch (e: any) { toast({ title: 'Failed', description: e.message, variant: 'destructive' }); }
  };
  const refresh = () => { if (id) getInterview(id).then(({ interview }) => setInterview(interview)).catch(() => {}); };
  const doRemerge = async (t: InterviewTake) => {
    if (!id) return;
    try { await remergeTake(id, t.id); toast({ title: 'Re-merging take…' }); refresh(); } catch (e: any) { toast({ title: 'Failed', description: e.message, variant: 'destructive' }); }
  };
  const doArchive = async (t: InterviewTake) => {
    if (!id) return;
    try { await patchTake(id, t.id, { archived: !t.archived }); refresh(); } catch (e: any) { toast({ title: 'Failed', description: e.message, variant: 'destructive' }); }
  };
  const src = (key: string) => (key.startsWith('http') || key.startsWith('/api/')) ? key : `/api/s3/${key}`;

  // Both uploads on the take, live: the phone's (reported by the guest page) and
  // ours. A clip that made it shows as done even when the other one failed.
  const latestTakeId = interview?.takes?.length ? interview.takes[interview.takes.length - 1].id : null;
  const uploadRows = (t: InterviewTake) => {
    const gs: any = interview?.guestState || {};
    const live = t.id === latestTakeId;
    const phone = t.guestClip?.status === 'ready' ? '✓ uploaded'
      : t.merged?.status === 'failed' && !t.guestClip?.s3Key ? "didn't upload"
      : live && gs.uploadFailed ? "didn't upload"
      : live && typeof gs.uploadPct === 'number' ? `${gs.uploadPct}%`
      : live && interview?.recordControl?.state === 'recording' ? 'recording…' : 'waiting…';
    const usesHost = t.mode !== 'guest_only' || (t as any).hostFailed;
    const mine = !usesHost ? null
      : t.hostClip?.status === 'ready' ? '✓ uploaded'
      : (t as any).hostFailed ? "didn't upload"
      : t.id === hostTakeRef.current && rec.uploadFailed ? "didn't upload"
      : t.id === hostTakeRef.current && typeof rec.uploadPct === 'number' ? `${rec.uploadPct}%`
      : t.id === hostTakeRef.current && rec.recording ? 'recording…' : 'waiting…';
    const tone = (v: string) => v.startsWith('✓') ? 'var(--hj-ink)' : v === "didn't upload" ? 'oklch(0.6 0.2 25)' : 'var(--hj-ink-2)';
    return (
      <div className="flex flex-wrap" style={{ gap: '2px 14px', padding: '6px 10px 0', fontSize: 11.5 }}>
        <span style={{ color: tone(phone) }}>Phone video: <strong>{phone}</strong></span>
        {mine && <span style={{ color: tone(mine) }}>Your audio: <strong>{mine}</strong></span>}
      </div>
    );
  };

  const renderTake = (t: InterviewTake, num: number, archived: boolean) => {
    const st = t.merged.status;
    const ready = st === 'ready' && t.merged.s3Key;
    // Raw full-res guest clip — playable the moment the upload lands, before
    // (and after) the encode. Casting shows the raw file first; so do we.
    const rawKey = t.guestClip?.s3Key || null;
    return (
      <div key={t.id} style={{ borderRadius: 10, border: '1px solid var(--hj-hairline)', overflow: 'hidden', background: 'var(--hj-surface-2)', opacity: archived ? 0.7 : 1 }}>
        {ready ? (
          <video src={src(t.merged.s3Key!)} controls poster={t.merged.thumbnailUrl ? src(t.merged.thumbnailUrl) : undefined} style={{ width: '100%', display: 'block', background: '#000', maxHeight: 200 }} />
        ) : rawKey && st !== 'failed' ? (
          <div>
            <video src={src(rawKey)} controls style={{ width: '100%', display: 'block', background: '#000', maxHeight: 200 }} />
            <div className="flex items-center gap-1.5" style={{ padding: '5px 10px', fontSize: 11, color: 'var(--hj-ink-3)' }}><Loader2 className="w-3 h-3 animate-spin" /> Original from the phone. {waitingLabel(t, false).replace('Clip is in. ', '')}</div>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-2 text-center" style={{ minHeight: 96, padding: '10px 12px', color: 'var(--hj-ink-3)', fontSize: 12.5 }}>
            {st === 'failed' ? <span style={{ color: 'oklch(0.6 0.2 25)' }}>Processing failed{t.merged.error ? ` — ${t.merged.error}` : ''}</span> : (<><Loader2 className="w-4 h-4 animate-spin" /> {waitingLabel(t, interview?.recordControl?.state === 'recording' && interview?.recordControl?.takeId === t.id)}</>)}
          </div>
        )}
        {uploadRows(t)}
        <div className="flex items-center gap-2" style={{ padding: '8px 10px' }}>
          <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--hj-ink)' }}>Take {num}</span>
          <span className="mono-label" style={{ color: 'var(--hj-ink-3)' }}>{MODES.find(m => m.key === t.mode)?.label}</span>
          {(t as any).hostFailed && <span style={{ fontSize: 11, color: 'var(--hj-ink-2)' }}>Your audio didn&rsquo;t upload; this uses the phone&rsquo;s audio.</span>}
          <div className="flex items-center gap-1.5" style={{ marginLeft: 'auto' }}>
            {ready && <a href={src(t.merged.s3Key!)} download title="Download" className="inline-flex items-center justify-center" style={{ width: 30, height: 30, borderRadius: 8, border: '1px solid var(--hj-hairline)', color: 'var(--hj-ink-2)' }}><Download className="w-3.5 h-3.5" /></a>}
            {rawKey && <a href={src(rawKey)} download title="Download full-res original" className="inline-flex items-center justify-center mono-label" style={{ height: 30, padding: '0 8px', borderRadius: 8, border: '1px solid var(--hj-hairline)', color: 'var(--hj-ink-2)', textDecoration: 'none', fontSize: 10 }}>RAW</a>}
            {(st === 'failed' || st === 'ready') && <button type="button" onClick={() => doRemerge(t)} title="Re-process" className="inline-flex items-center justify-center" style={{ width: 30, height: 30, borderRadius: 8, border: '1px solid var(--hj-hairline)', background: 'transparent', color: 'var(--hj-ink-2)', cursor: 'pointer' }}><RotateCcw className="w-3.5 h-3.5" /></button>}
            <button type="button" onClick={() => doArchive(t)} title={archived ? 'Restore' : 'Archive'} className="inline-flex items-center justify-center" style={{ width: 30, height: 30, borderRadius: 8, border: '1px solid var(--hj-hairline)', background: 'transparent', color: 'var(--hj-ink-3)', cursor: 'pointer' }}>{archived ? <ArchiveRestore className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}</button>
          </div>
        </div>
      </div>
    );
  };

  // Compact clickable thumbnail tile for the grouped grid / modal filmstrip.
  const renderThumb = (t: InterviewTake, num: number, groupKey: string, opts?: { active?: boolean }) => {
    const st = t.merged.status;
    const ready = st === 'ready' && t.merged.s3Key;
    const thumb = ready && t.merged.thumbnailUrl ? src(t.merged.thumbnailUrl) : null;
    return (
      <button key={t.id} type="button" onClick={() => { setModalGroup(groupKey); setModalTakeId(t.id); }}
        className="relative" style={{ display: 'block', width: '100%', aspectRatio: '16/9', borderRadius: 10, overflow: 'hidden', padding: 0, cursor: 'pointer', background: '#000', border: opts?.active ? '2px solid var(--hj-blue)' : '1px solid var(--hj-hairline)' }}>
        {thumb ? (
          <img src={thumb} alt={`Take ${num}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : t.guestClip?.s3Key && st !== 'failed' ? (
          // The phone's original is already here: show it, not a spinner.
          <video src={`${src(t.guestClip.s3Key)}#t=0.1`} muted playsInline preload="metadata" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-center" style={{ color: 'var(--hj-ink-3)', fontSize: 11 }}>
            {st === 'failed' ? <span style={{ color: 'oklch(0.65 0.2 25)' }}>Failed</span> : <Loader2 className="w-4 h-4 animate-spin" />}
          </div>
        )}
        {ready && <span className="absolute inset-0 flex items-center justify-center" style={{ background: 'oklch(0 0 0 / 0.15)' }}><Play className="w-6 h-6" style={{ color: '#fff', fill: '#fff', opacity: 0.9 }} /></span>}
        <span className="absolute" style={{ left: 6, bottom: 6, padding: '2px 6px', borderRadius: 6, background: 'oklch(0 0 0 / 0.6)', color: '#fff', fontSize: 10.5, fontWeight: 700 }}>Take {num}</span>
      </button>
    );
  };

  if (loading) return <main className="min-h-screen flex items-center justify-center" style={{ background: 'var(--hj-bg)' }}><Loader2 className="w-7 h-7 animate-spin" style={{ color: 'var(--hj-ink-3)' }} /></main>;

  // Group active takes by the guest they were recorded with (chronological).
  const guestKeyOf = (t: InterviewTake) => (t.guestName || interview?.guestName || 'Guest');
  const activeTakes = interview ? interview.takes.filter(t => !t.archived) : [];
  const archivedTakes = interview ? interview.takes.filter(t => t.archived) : [];
  const groupsMap = new Map<string, InterviewTake[]>();
  for (const t of activeTakes) { const k = guestKeyOf(t); if (!groupsMap.has(k)) groupsMap.set(k, []); groupsMap.get(k)!.push(t); }
  const activeGroups = [...groupsMap.entries()].map(([key, takes]) => ({ key, takes }));
  const modalTakes = modalGroup ? (groupsMap.get(modalGroup) || []) : [];
  const modalTake = modalTakes.find(t => t.id === modalTakeId) || modalTakes[0] || null;
  const closeModal = () => { setModalGroup(null); setModalTakeId(null); };

  return (
    <main className="min-h-screen lg:h-screen lg:overflow-hidden flex flex-col" style={{ background: 'var(--hj-bg)', color: 'var(--hj-ink)' }}>
      <div className="flex items-center gap-3 flex-shrink-0" style={{ padding: '14px 20px', borderBottom: '1px solid var(--hj-hairline-soft)' }}>
        {/* Back goes to wherever this room belongs: a reader session belongs to the
            reader's sessions page, not the store. */}
        <button type="button" onClick={() => navigate(
          (interview as any)?.kind === 'reader_session' ? '/readers/requests?tab=reading'
          : new URLSearchParams(window.location.search).get('from') === 'library' ? '/my-library'
          : interview?.productId ? `/my-store/course/${interview.productId}` : '/my-store'
        )} title={(interview as any)?.kind === 'reader_session' ? 'Back to my reader sessions' : 'Back'} className="inline-flex items-center justify-center" style={{ width: 34, height: 34, borderRadius: 999, background: 'var(--hj-surface-2)', border: '1px solid var(--hj-hairline)', color: 'var(--hj-ink-2)', cursor: 'pointer' }}><ChevronLeft className="w-4 h-4" /></button>
        <div>
          <div className="mono-label" style={{ color: 'var(--hj-ink-3)' }}>{(interview as any)?.kind === 'reader_session' ? 'Reader session' : 'Interview'}</div>
          <div className="font-hj-display" style={{ fontSize: 17, fontWeight: 700, letterSpacing: '-0.02em' }}>{interview?.title}</div>
        </div>
      </div>

      <div className="flex-1 flex flex-col lg:flex-row min-h-0">
        {/* Call — use the moderator JWT when configured; otherwise fall back to a
            bare join so the host can still test the call (just not as moderator). */}
        <div className="flex-1 min-h-[380px] relative" style={{ background: '#000' }}>
          {(() => {
            const eff = join || (interview ? { domain: 'meet.heyjoe.io', roomName: interview.roomName, jwt: '' } : null);
            if (!eff) return <div className="w-full h-full flex items-center justify-center"><Loader2 className="w-7 h-7 animate-spin" style={{ color: 'var(--hj-ink-3)' }} /></div>;
            return (
              <>
                <JitsiCall domain={eff.domain} roomName={eff.roomName} jwt={eff.jwt || undefined} displayName="Host" />
              </>
            );
          })()}
        </div>

        {/* Right rail */}
        <div className="flex flex-col gap-4 flex-shrink-0 overflow-y-auto" style={{ width: '100%', maxWidth: 380, padding: 20, borderLeft: '1px solid var(--hj-hairline-soft)' }}>
          {/* Invite */}
          <div style={card}>
            <div className="mono-label" style={{ color: 'var(--hj-ink-3)', marginBottom: 8 }}>Invite your guest</div>
            <div className="flex items-center gap-1.5">
              <button type="button" onClick={() => copy(`${guestUrl}\n\n${INVITE_BLURB}`, 'Invite copied')} className="flex-1 whitespace-nowrap" style={{ ...ghost, padding: '7px 8px', justifyContent: 'center', background: 'var(--hj-blue)', border: '1px solid var(--hj-blue)', color: 'oklch(0.18 0.005 70)' }}><Copy className="w-3.5 h-3.5" /> Invite</button>
              <button type="button" onClick={() => copy(guestUrl, 'Link copied')} className="flex-1 whitespace-nowrap" style={{ ...ghost, padding: '7px 8px', justifyContent: 'center' }}><LinkIcon className="w-3.5 h-3.5" /> Link</button>
              <button type="button" onClick={doRegen} className="flex-1 whitespace-nowrap" style={{ ...ghost, padding: '7px 8px', justifyContent: 'center' }}><RefreshCw className="w-3.5 h-3.5" /> New</button>
            </div>
          </div>

          {/* Guest status */}
          <div style={card}>
            <div className="mono-label flex items-center gap-1.5" style={{ color: 'var(--hj-ink-3)', marginBottom: 8 }}><Users className="w-3.5 h-3.5" /> Guest</div>
            <div className="flex items-center gap-2" style={{ fontSize: 14, color: 'var(--hj-ink)' }}>
              {gs?.inCall ? <CheckCircle className="w-4 h-4" style={{ color: 'var(--hj-green)' }} /> : <Circle className="w-4 h-4" style={{ color: 'var(--hj-ink-4)' }} />}
              <span>{interview?.guestName || 'No one yet'}</span>
              <span className="mono-label" style={{ marginLeft: 'auto', color: 'var(--hj-ink-3)' }}>
                {(gs?.recording || (Date.now() - appRecLiveAt < 4000)) ? '● REC' : appUploadPct != null ? `Uploading ${appUploadPct}%` : gs?.uploadPct != null ? `Uploading ${gs.uploadPct}%` : gs?.camReady ? 'Camera ready' : gs?.inCall ? 'In call' : 'Waiting'}
              </span>
            </div>
            {/* Web guest on a phone/tablet browser; a stale heartbeat means they left (maybe for the app). */}
            {gs?.mobileBrowser && interview?.guestLastSeenAt && Date.now() - new Date(interview.guestLastSeenAt).getTime() < 20000 && (
              <div className="flex gap-2" style={{ marginTop: 10, padding: '9px 11px', borderRadius: 'var(--hj-r-sm)', background: 'oklch(0.85 0.15 85)', color: 'oklch(0.18 0.005 70)', fontSize: 13, fontWeight: 600, lineHeight: 1.4 }}>
                <AlertTriangle className="w-4 h-4 flex-shrink-0" style={{ marginTop: 1 }} />
                <span>{interview?.guestName || 'Your guest'} is recording in a phone or tablet browser, so takes will be lower quality. Ask them to open the link in the Hey Joe app.</span>
              </div>
            )}
          </div>

          {/* Record */}
          <div style={card}>
            <div className="mono-label" style={{ color: 'var(--hj-ink-3)', marginBottom: 10 }}>Record</div>
            <div className="flex gap-1.5 flex-wrap" style={{ marginBottom: 12 }}>
              {MODES.map(m => (
                <button key={m.key} type="button" onClick={() => setMode(m.key)} disabled={recording} style={{ padding: '6px 11px', borderRadius: 999, fontSize: 12, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', border: `1px solid ${mode === m.key ? 'var(--hj-blue)' : 'var(--hj-hairline)'}`, background: mode === m.key ? 'var(--hj-blue)' : 'transparent', color: mode === m.key ? 'oklch(0.18 0.005 70)' : 'var(--hj-ink-2)' }}>{m.label}</button>
              ))}
            </div>
            {!recording ? (
              <button type="button" onClick={doStart} disabled={!gs?.camReady} className="w-full inline-flex items-center justify-center gap-2" style={{ padding: '11px', borderRadius: 999, background: 'oklch(0.6 0.2 25)', border: 0, color: '#fff', fontSize: 14, fontWeight: 700, cursor: gs?.camReady ? 'pointer' : 'not-allowed', opacity: gs?.camReady ? 1 : 0.5 }}>
                <Circle className="w-4 h-4" style={{ fill: '#fff' }} /> Record
              </button>
            ) : (
              <button type="button" onClick={doStop} className="w-full inline-flex items-center justify-center gap-2" style={{ padding: '11px', borderRadius: 999, background: 'var(--hj-surface-2)', border: '1px solid var(--hj-hairline)', color: 'var(--hj-ink)', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                <span style={{ width: 12, height: 12, borderRadius: 2, background: 'oklch(0.6 0.2 25)' }} /> Stop
                {rc?.startAtEpochMs ? <span className="tabular" style={{ fontFamily: 'var(--hj-font-mono)', fontWeight: 600, fontSize: 13 }}>{(() => { const sec = Math.max(0, Math.floor((Date.now() - rc.startAtEpochMs) / 1000)); return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`; })()}</span> : null}
              </button>
            )}
            {!gs?.camReady && <p className="m-0" style={{ marginTop: 8, fontSize: 11.5, color: 'var(--hj-ink-3)' }}>Enabled once your guest's camera is ready.</p>}
            {rec.recording && <p className="m-0" style={{ marginTop: 8, fontSize: 11.5, color: 'oklch(0.6 0.2 25)', fontWeight: 600 }}>● Recording your camera</p>}
            {!rec.recording && rec.uploadPct != null && rec.uploadPct < 100 && <p className="m-0" style={{ marginTop: 8, fontSize: 11.5, color: 'var(--hj-ink-2)' }}>Uploading your clip — {rec.uploadPct}%</p>}
            <p className="m-0" style={{ marginTop: 8, fontSize: 11.5, color: 'var(--hj-ink-2)' }}>Each side records locally at full quality; we merge the take after you stop.</p>
          </div>

          {/* Gimbal — appears only when an app guest's phone reports one */}
          {interview?.casting?.recordId && <GimbalPanel casting={interview.casting} />}

          {/* Takes — grouped by guest, thumbnails open a player modal */}
          {activeGroups.map(g => (
            <div style={card} key={g.key}>
              <div className="flex items-center gap-2" style={{ marginBottom: 10 }}>
                <span className="mono-label" style={{ color: 'var(--hj-ink-3)' }}>{g.key}</span>
                <span className="mono-label" style={{ marginLeft: 'auto', color: 'var(--hj-ink-4)' }}>{g.takes.length} take{g.takes.length === 1 ? '' : 's'}</span>
              </div>
              <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
                {g.takes.map((t, i) => renderThumb(t, i + 1, g.key))}
              </div>
            </div>
          ))}
          {archivedTakes.length > 0 && (
            <div style={card}>
              <button type="button" onClick={() => setShowArchived(v => !v)} className="w-full flex items-center gap-2" style={{ background: 'transparent', border: 0, padding: 0, cursor: 'pointer', color: 'var(--hj-ink-3)' }}>
                <span className="mono-label">Archived ({archivedTakes.length})</span>
                <ChevronDown className="w-4 h-4" style={{ marginLeft: 'auto', transform: showArchived ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
              </button>
              {showArchived && (
                <div className="flex flex-col gap-3" style={{ marginTop: 10 }}>
                  {archivedTakes.map((t, i) => renderTake(t, archivedTakes.length - i, true))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Take player modal — big player + filmstrip of the guest's takes */}
      {modalGroup && modalTake && (() => {
        const st = modalTake.merged.status;
        const ready = st === 'ready' && modalTake.merged.s3Key;
        const num = modalTakes.findIndex(t => t.id === modalTake.id) + 1;
        return (
          <div className="fixed inset-0 flex items-center justify-center" style={{ zIndex: 60, background: 'oklch(0 0 0 / 0.72)', padding: 20 }} onClick={closeModal}>
            <div className="flex flex-col" style={{ width: '100%', maxWidth: 860, maxHeight: 'calc(100dvh - 40px)', background: 'var(--hj-surface-1)', border: '1px solid var(--hj-hairline)', borderRadius: 'var(--hj-r-md)', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
              <div className="flex flex-wrap items-center gap-2 flex-shrink-0" style={{ padding: '12px 16px', borderBottom: '1px solid var(--hj-hairline-soft)' }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--hj-ink)' }}>{modalGroup}</span>
                <span className="mono-label" style={{ color: 'var(--hj-ink-3)' }}>Take {num} · {MODES.find(m => m.key === modalTake.mode)?.label}</span>
                <div className="flex items-center gap-1.5" style={{ marginLeft: 'auto' }}>
                  {ready && <a href={src(modalTake.merged.s3Key!)} download title="Download" className="inline-flex items-center justify-center" style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid var(--hj-hairline)', color: 'var(--hj-ink-2)' }}><Download className="w-4 h-4" /></a>}
                  {modalTake.guestClip?.s3Key && <a href={src(modalTake.guestClip.s3Key)} download title="Download full-res original" className="inline-flex items-center justify-center mono-label" style={{ height: 32, padding: '0 9px', borderRadius: 8, border: '1px solid var(--hj-hairline)', color: 'var(--hj-ink-2)', textDecoration: 'none', fontSize: 10.5 }}>RAW</a>}
                  {(st === 'failed' || st === 'ready') && <button type="button" onClick={() => doRemerge(modalTake)} title="Re-process" className="inline-flex items-center justify-center" style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid var(--hj-hairline)', background: 'transparent', color: 'var(--hj-ink-2)', cursor: 'pointer' }}><RotateCcw className="w-4 h-4" /></button>}
                  <button type="button" onClick={() => { doArchive(modalTake); closeModal(); }} title="Archive" className="inline-flex items-center justify-center" style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid var(--hj-hairline)', background: 'transparent', color: 'var(--hj-ink-3)', cursor: 'pointer' }}><Archive className="w-4 h-4" /></button>
                  <button type="button" onClick={closeModal} title="Close" className="inline-flex items-center justify-center" style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid var(--hj-hairline)', background: 'transparent', color: 'var(--hj-ink-2)', cursor: 'pointer' }}><X className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="flex items-center justify-center" style={{ background: '#000', flex: 1, minHeight: 0 }}>
                {(ready || (modalTake.guestClip?.s3Key && st !== 'failed')) ? (
                  <div>
                    <video key={`${modalTake.id}-${ready ? 'enc' : 'raw'}`} src={src((ready ? modalTake.merged.s3Key : modalTake.guestClip?.s3Key)!)} controls autoPlay poster={ready && modalTake.merged.thumbnailUrl ? src(modalTake.merged.thumbnailUrl) : undefined} style={{ width: '100%', maxHeight: '64vh', display: 'block' }} />
                    {!ready && <div className="flex items-center gap-1.5" style={{ padding: '6px 12px', fontSize: 11.5, color: 'var(--hj-ink-3)' }}><Loader2 className="w-3 h-3 animate-spin" /> Original from the phone. {waitingLabel(modalTake, false).replace('Clip is in. ', '')}</div>}
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-2 text-center" style={{ minHeight: 240, padding: 20, color: 'var(--hj-ink-3)', fontSize: 13 }}>
                    {st === 'failed' ? <span style={{ color: 'oklch(0.65 0.2 25)' }}>Processing failed{modalTake.merged.error ? ` — ${modalTake.merged.error}` : ''}</span> : (<><Loader2 className="w-5 h-5 animate-spin" /> {waitingLabel(modalTake, interview?.recordControl?.state === 'recording' && interview?.recordControl?.takeId === modalTake.id)}</>)}
                  </div>
                )}
              </div>
              {modalTakes.length > 1 && (
                <div className="flex gap-2 flex-shrink-0 overflow-x-auto" style={{ padding: 12, borderTop: '1px solid var(--hj-hairline-soft)' }}>
                  {modalTakes.map((t, i) => (
                    <div key={t.id} style={{ width: 110, flexShrink: 0 }}>{renderThumb(t, i + 1, modalGroup, { active: t.id === modalTake.id })}</div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })()}
    </main>
  );
};

export default InterviewHostPage;
